import React from 'react';
import GuidelineSection from '../components/GuidelineSection';
import SubSection from '../components/SubSection';
import Table from '../components/Table';
import TableOfContents from '../components/TableOfContents';
const NCCNOptions: React.FC = () => {
  const tocItems = [{
    id: 'introduction',
    title: 'Introduction',
    level: 1
  }, {
    id: 'targeted-therapies',
    title: 'Targeted Therapies',
    level: 1
  }, {
    id: 'immunotherapies',
    title: 'Immunotherapies',
    level: 1
  }, {
    id: 'cellular-therapies',
    title: 'Cellular Therapies',
    level: 1
  }, {
    id: 'antibodies-adcs',
    title: 'Antibodies and ADCs',
    level: 1
  }, {
    id: 'chemotherapies',
    title: 'Chemotherapies',
    level: 1
  }, {
    id: 'other-options',
    title: 'Other NCCN Options',
    level: 1
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-375">
      <div className="lg:col-span-3" data-id="element-376">
        <GuidelineSection id="nccn-options" title="1. NCCN OPTIONS" data-id="element-377">
          <SubSection id="introduction" title="1.1 Introduction" data-id="element-378">
            <p className="mb-4" data-id="element-379">
              The purpose of the NCCN Options section of the PCCN Guidelines is
              to ensure that all the options available to you under the NCCN
              Guidelines have been explored. Each section has a "Completeness
              check" that describes how to be sure all NCCN options have been
              explored.
            </p>
          </SubSection>
          <SubSection id="targeted-therapies" title="1.2 Targeted Therapies" data-id="element-380">
            <p className="mb-4" data-id="element-381">
              Table: Biomarkers and Therapies in NCCN Guidelines for NSCLC
            </p>
            <Table headers={['Biomarker', 'FDA-Approved Therapies', 'Line of Therapy', 'Evidence Level']} rows={[['EGFR Exon 19 del/L858R', 'Osimertinib[1], Erlotinib[2], Gefitinib[3], Afatinib[4]', 'First-line', 'Strong (Green)'], ['EGFR Exon 20 insertion', 'Amivantamab[5], Sunvozertinib[6]', 'First-line/Second-line+', 'Strong (Green)'], ['ALK rearrangement', 'Alectinib[7], Brigatinib[8], Lorlatinib[9], Ceritinib[10], Crizotinib[11]', 'First-line', 'Strong (Green)'], ['ROS1 rearrangement', 'Entrectinib[12], Crizotinib[13], Repotrectinib[14]', 'First-line', 'Strong (Green)'], ['BRAF V600E', 'Dabrafenib + Trametinib[15]', 'First/Second-line', 'Strong (Green)'], ['KRAS G12C', 'Sotorasib[16], Adagrasib[17]', 'Second-line+', 'Strong (Green)'], ['MET Exon 14 skipping', 'Capmatinib[18], Tepotinib[19], Crizotinib[20]', 'First/Second-line', 'Strong (Green)'], ['MET amplification (high)', 'Capmatinib[21], Tepotinib[22]', 'Second-line+', 'Moderate (Orange)'], ['RET rearrangement', 'Selpercatinib[23], Pralsetinib[24]', 'First-line', 'Strong (Green)'], ['NTRK fusion', 'Larotrectinib[25], Entrectinib[26]', 'Tissue-agnostic', 'Strong (Green)'], ['HER2 mutations', 'Fam-trastuzumab deruxtecan-nxki (Enhertu)[27]', 'Second-line+', 'Strong (Green)'], ['NRG1 fusion', 'Zenocutuzumab[28]', 'Any line', 'Strong (Green)']]} data-id="element-382" />
            <div className="bg-blue-50 p-4 rounded-lg mt-6 mb-6" data-id="element-383">
              <h4 className="font-semibold text-blue-800 mb-2" data-id="element-384">
                Completeness Check:
              </h4>
              <ul className="list-none space-y-2" data-id="element-385">
                <li className="flex items-start" data-id="element-386">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-387" />
                  <span data-id="element-388">
                    Make sure your genomic sequencing included all these
                    mutations. Comprehensive next-generation sequencing (NGS)
                    panels such as Foundation Medicine, Guardant360, or Tempus
                    should test for the entire set of mutations listed above.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-389">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-390" />
                  <span data-id="element-391">
                    If your testing was limited to single-gene tests, you may
                    have missed actionable mutations. Consider comprehensive NGS
                    testing if not already completed.
                  </span>
                </li>
              </ul>
            </div>
            <p className="mb-4" data-id="element-392">
              <strong data-id="element-393">Note on Drug Sensitivity:</strong> Drugs in the same
              categories (for example, the EGFR inhibitors osimertinib,
              erlotinib, and gefitinib) are considered by many oncologists to be
              virtually the same drug, but Drug Sensitivity Tests (DSTs) have
              shown the patient's live cancer cells may respond differently to
              different EGFR inhibitors. Consider using a DST to determine which
              particular drug in a group of related drugs is most likely to
              affect your cancer.
            </p>
          </SubSection>
          <SubSection id="immunotherapies" title="1.3 Immunotherapies" data-id="element-394">
            <p className="mb-4" data-id="element-395">
              Table: Immunotherapy Biomarkers and Therapies
            </p>
            <Table headers={['Biomarker', 'Therapies', 'Setting', 'Evidence Level']} rows={[['PD-L1 ≥50%', 'Pembrolizumab monotherapy[29]', 'First-line', 'Strong (Green)'], ['PD-L1 1-49%', 'Pembrolizumab + chemotherapy[30]', 'First-line', 'Strong (Green)'], ['PD-L1 <1%', 'Pembrolizumab + chemotherapy[31]', 'First-line', 'Moderate (Orange)'], ['TMB-High (≥10 mut/Mb)', 'Pembrolizumab[32]', 'Second-line+', 'Moderate (Orange)'], ['MSI-High/dMMR', 'Pembrolizumab[33]', 'Tissue-agnostic', 'Strong (Green)'], ['Any PD-L1', 'Nivolumab (post-chemo)[34]', 'Second-line+', 'Strong (Green)'], ['Stage III unresectable', 'Durvalumab (post-chemoradiation)[35]', 'Stage III', 'Strong (Green)']]} data-id="element-396" />
            <div className="bg-yellow-50 p-4 rounded-lg mt-6 mb-6" data-id="element-397">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-398">
                Important Note on Atezolizumab:
              </h4>
              <p data-id="element-399">
                Atezolizumab does not have a standalone second-line monotherapy
                approval for NSCLC. It is approved for first-line treatment in
                combination with chemotherapy (with or without bevacizumab) for
                metastatic disease, and as monotherapy for patients with high
                PD-L1 expression (TC ≥50% or IC ≥10%).[36] It also has an
                adjuvant approval for stage II-IIIA NSCLC with PD-L1 ≥1%
                following resection and platinum-based chemotherapy.[37]
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg mt-6 mb-6" data-id="element-400">
              <h4 className="font-semibold text-blue-800 mb-2" data-id="element-401">
                Completeness Check:
              </h4>
              <ul className="list-none space-y-2" data-id="element-402">
                <li className="flex items-start" data-id="element-403">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-404" />
                  <span data-id="element-405">
                    Make sure your testing includes TMB (Tumor Mutational
                    Burden), MSI (Microsatellite Instability), and PD-L1, the
                    immunotherapy biomarkers.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-406">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-407" />
                  <span data-id="element-408">
                    These should be included in comprehensive NGS panels or can
                    be ordered separately. FoundationOne CDx is approved as a
                    companion diagnostic for TMB assessment.[38]
                  </span>
                </li>
              </ul>
            </div>
          </SubSection>
          <SubSection id="cellular-therapies" title="1.4 Cellular Therapies" data-id="element-409">
            <p className="mb-4" data-id="element-410">
              There are currently no FDA-approved cellular therapies
              specifically for NSCLC outside of clinical trials. However, you
              may be eligible for cellular therapies including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-411">
              <li data-id="element-412">
                CAR-T cell therapies targeting solid tumor antigens (Phase I/II
                trials)
              </li>
              <li data-id="element-413">
                Tumor Infiltrating Lymphocytes (TIL) therapy (investigational)
              </li>
              <li data-id="element-414">NK cell therapies combined with checkpoint inhibitors</li>
            </ul>
            <p data-id="element-415">
              Please see the Clinical Trials section below for current
              opportunities.
            </p>
          </SubSection>
          <SubSection id="antibodies-adcs" title="1.5 Antibodies and ADCs (Antibody-Drug Conjugates)" data-id="element-416">
            <div className="bg-yellow-50 p-4 rounded-lg mb-6" data-id="element-417">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-418">
                Important Classification Note:
              </h4>
              <p data-id="element-419">
                Not all targeted antibodies are ADCs. Bispecific antibodies like
                amivantamab work through different mechanisms without cytotoxic
                payloads.[39]
              </p>
            </div>
            <p className="mb-4" data-id="element-420">
              Table: Antibodies and ADCs in NCCN Guidelines
            </p>
            <Table headers={['Drug Name', 'Type', 'Target', 'Indication', 'Evidence Level']} rows={[[<span className="font-semibold" data-id="element-422">Bispecific Antibodies</span>, '', '', '', ''], ['Amivantamab[40]', 'Bispecific Antibody', 'EGFR/MET', 'EGFR Exon 20 insertion; EGFR-mutated (with lazertinib, first-line)', 'Strong (Green)'], [<span className="font-semibold" data-id="element-423">
                    Antibody-Drug Conjugates (ADCs)
                  </span>, '', '', '', ''], ['Datopotamab deruxtecan[41]', 'ADC', 'TROP2', 'EGFR-mutated NSCLC (post-EGFR TKI + chemo)', 'Strong (Green)'], ['Telisotuzumab vedotin (Emrelis)[42]', 'ADC', 'c-MET', 'Nonsquamous NSCLC with high c-MET overexpression', 'Strong (Green)'], ['Fam-trastuzumab deruxtecan[43]', 'ADC', 'HER2', 'HER2-mutated advanced NSCLC', 'Strong (Green)'], [<span className="font-semibold" data-id="element-424">Other Antibodies</span>, '', '', '', ''], ['Necitumumab[44]', 'mAb', 'EGFR', 'Squamous NSCLC + chemotherapy', 'Moderate (Orange)'], ['Ramucirumab[45]', 'mAb', 'VEGFR2', 'Second-line with docetaxel', 'Moderate (Orange)'], ['Bevacizumab[46]', 'mAb', 'VEGF', 'Nonsquamous NSCLC + chemotherapy', 'Strong (Green)']]} data-id="element-421" />
            <div className="bg-yellow-50 p-4 rounded-lg mt-6 mb-6" data-id="element-425">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-426">
                Note on Recent Approvals:
              </h4>
              <ul className="list-disc pl-6 space-y-2" data-id="element-427">
                <li data-id="element-428">
                  Datopotamab deruxtecan received FDA accelerated approval on
                  June 23, 2025, specifically for EGFR-mutated NSCLC after prior
                  EGFR-directed therapy AND platinum-based chemotherapy. The
                  approval was based on pooled analysis from TROPION-Lung05 and
                  TROPION-Lung01, showing 45% objective response rate with 6.5
                  months median duration of response.[47]
                </li>
                <li data-id="element-429">
                  Telisotuzumab vedotin (Emrelis) received FDA accelerated
                  approval on May 14, 2025, as the first c-MET-targeted ADC and
                  first treatment specifically for NSCLC with high c-MET protein
                  overexpression (≥50% of tumor cells with IHC 3+ staining). The
                  VENTANA MET (SP44) RxDx Assay is required as companion
                  diagnostic.[48]
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg mt-6 mb-6" data-id="element-430">
              <h4 className="font-semibold text-blue-800 mb-2" data-id="element-431">
                Completeness Check:
              </h4>
              <ul className="list-none space-y-2" data-id="element-432">
                <li className="flex items-start" data-id="element-433">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-434" />
                  <span data-id="element-435">
                    If you have not received any of these advanced treatments
                    and have the appropriate biomarkers, discuss these options
                    with your oncologist.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-436">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-437" />
                  <span data-id="element-438">
                    ADCs represent a newer class of targeted therapy combining
                    antibodies with chemotherapy payloads for more precise
                    cancer cell killing.
                  </span>
                </li>
              </ul>
            </div>
          </SubSection>
          <SubSection id="chemotherapies" title="1.6 Chemotherapies" data-id="element-439">
            <p className="mb-4" data-id="element-440">
              Table: Standard Chemotherapy Regimens for NSCLC
            </p>
            <Table headers={['Regimen', 'Indication', 'Line', 'Evidence Level']} rows={[['Carboplatin + Pemetrexed', 'Nonsquamous NSCLC', 'First-line', 'Strong (Green)'], ['Cisplatin + Pemetrexed', 'Nonsquamous NSCLC', 'First-line', 'Strong (Green)'], ['Carboplatin + Paclitaxel', 'Any histology', 'First-line', 'Strong (Green)'], ['Carboplatin + nab-Paclitaxel', 'Any histology', 'First-line', 'Strong (Green)'], ['Cisplatin + Gemcitabine', 'Squamous NSCLC', 'First-line', 'Strong (Green)'], ['Docetaxel monotherapy', 'Any histology', 'Second-line', 'Strong (Green)'], ['Pemetrexed monotherapy', 'Nonsquamous NSCLC', 'Second-line', 'Strong (Green)'], ['Gemcitabine monotherapy', 'Any histology', 'Second-line', 'Moderate (Orange)']]} data-id="element-441" />
            <p className="mt-4" data-id="element-442">
              <strong data-id="element-443">Note:</strong> If you have not received any of these basic
              treatments, you may want to discuss these treatments with your
              oncologist. Drugs in the same categories (for example, the
              'platinum' drugs—cisplatin, carboplatin, and oxaliplatin) are
              considered by many oncologists to be virtually the same drug, but
              Drug Sensitivity Tests (DSTs) have shown the patient's live cancer
              cells may respond differently to different platinum drugs.
              Consider using a DST to determine which particular drug in a group
              of related drugs is most likely to affect your cancer.
            </p>
          </SubSection>
          <SubSection id="other-options" title="1.7 Other NCCN Options" data-id="element-444">
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-445">
              <li data-id="element-446">
                <strong data-id="element-447">Radiation Therapy:</strong> SBRT (Stereotactic Body
                Radiation Therapy) for oligometastatic disease
              </li>
              <li data-id="element-448">
                <strong data-id="element-449">Surgical Options:</strong> Lobectomy, wedge resection
                for early-stage disease
              </li>
              <li data-id="element-450">
                <strong data-id="element-451">Perioperative Treatments:</strong> Neoadjuvant
                chemoimmunotherapy (CheckMate 816 regimen)[49]
              </li>
              <li data-id="element-452">
                <strong data-id="element-453">Maintenance Therapy:</strong> Pemetrexed or
                immunotherapy continuation after first-line treatment
              </li>
            </ul>
            <div className="bg-blue-50 p-4 rounded-lg mt-6 mb-6" data-id="element-454">
              <h4 className="font-semibold text-blue-800 mb-2" data-id="element-455">
                Completeness Check:
              </h4>
              <ul className="list-none" data-id="element-456">
                <li className="flex items-start" data-id="element-457">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-458" />
                  <span data-id="element-459">
                    Review with your oncologist whether you have explored all
                    appropriate treatment modalities for your stage and
                    biomarker profile.
                  </span>
                </li>
              </ul>
            </div>
          </SubSection>
        </GuidelineSection>
      </div>
      <div className="lg:col-span-1" data-id="element-460">
        <div className="lg:sticky lg:top-20" data-id="element-461">
          <TableOfContents items={tocItems} data-id="element-462" />
        </div>
      </div>
    </div>;
};
export default NCCNOptions;